import invariant from 'invariant'
import * as React from 'react'
import type {ComponentType, ReactElement, ReactNode} from 'react'

function isFunction(props: unknown): props is Function {
  return typeof props === 'function'
}

/**
 * Creates new element, or clones existing one depending on type of `component`
 *
 * @param component - React component, stateless function or <Element/>
 * @param propsOrFunction - Props that will be merged with element's props, or function.
 *  If component is created, not cloned, propsOrFunction is called without props (void)

 * @warning Please bear in mind, that created elements will have all their defaultProps set already!
 */
export default function cloneOrCreateElement<P>(
  component: string | Function | ReactElement,
  propsOrFunction?: null | Function | P
) {
  invariant(arguments.length < 3, 'Pass children along with props')
  const isElement = React.isValidElement(component)
  let props

  if (isFunction(propsOrFunction)) {
    if (isElement) {
      props = (component as ReactElement).props
    }
    props = propsOrFunction(props)
  } else {
    props = propsOrFunction
  }

  if (isElement) {
    if (props && props !== (component as ReactElement).props) {
      return React.cloneElement<P>(component as ReactElement, props)
    }
    return component
  }
  return React.createElement<P>(component as any, props)
}

// @deprecate
export const cloneOrCreate = cloneOrCreateElement

/** Returns TRUE for pure functions that are not react stateless components.
 * These functions can be called directly and inlined for better performance */
export function isPureFunction(func): func is Function {
  if (typeof func !== 'function') {
    return false
  }
  const componentProto = func.prototype
  return (
    !(componentProto && componentProto.render) &&
    !func.defaultProps &&
    !func.displayName &&
    !func.propTypes &&
    !func.contextTypes
  )
}

export const isClassComponent = (component: any): boolean => {
  return component.prototype && typeof component.prototype.render === 'function'
}
export const isClassComponentOrElement = (componentOrElement: any): boolean => {
  const component = React.isValidElement(componentOrElement)
    ? componentOrElement.type
    : componentOrElement
  return isClassComponent(component)
}

/**
 * Creates new element or calls a function if it's pure (no displayName, propTypes, defaultProps or context)
 *
 * New elements are created for React.Component descendants, or pure functions with defined propTypes or contextTypes
 *
 * */
export function createOrCallElement<P>(
  component: string | Function,
  props?: P
) {
  /** Pure functions without propTypes and contextTypes may be called in-place for better performance and shallower component tree */
  if (isPureFunction(component)) {
    return component(props || {}) as React.ReactElement<P>
  }
  return React.createElement<P>(component, props || ({} as P))
}

export function isSimpleNode(node: any): node is React.ReactNode {
  return !node || typeof node === 'string' || Array.isArray(node)
}

/**
 * Creates new element, clones existing one or calls a function depending on type of `componentOrFunction`
 *
 * New elements are created for React.Component descendants, or pure functions with defined propTypes or contextTypes
 *
 * */
export function cloneCreateOrCallElement<P>(
  elementOrComponent: string | ComponentType<any> | ReactElement | Function,
  propsOrFunction?: null | Function | P
) {
  const isElement = React.isValidElement(elementOrComponent)
  let props

  if (isFunction(propsOrFunction)) {
    if (isElement) {
      props = (elementOrComponent as ReactElement).props
    }
    props = propsOrFunction(props)
  } else {
    props = propsOrFunction
  }

  if (isElement) {
    if (props && props !== (elementOrComponent as ReactElement).props) {
      return React.cloneElement<P>(elementOrComponent as ReactElement, props)
    }
    // Just return the element as-is
    return elementOrComponent
  }

  return createOrCallElement<P>(elementOrComponent, props)
}

// @deprecate
export const cloneCreateOrCall = cloneCreateOrCallElement

/** Special variant of cloneCreateOrCall that works well with nodes (eg. children).
 * null, string or Array will be returned as-is.
 * React elements will be cloned
 * Functions/components will be created.
 * */
export function cloneCreateOrCallNode<P>(
  node: ReactNode,
  props?: P
): ReactNode {
  if (isSimpleNode(node)) return node
  return cloneCreateOrCallElement<P>(node, props)
}

export function cloneOrCreateNode<P>(node: ReactNode, props?: P) {
  if (isSimpleNode(node)) return node
  return cloneOrCreateElement<P>(node, props)
}

export const cloneCreateOrCallChildren = cloneCreateOrCallNode

/**
 * Inlines a Stateless Functional Component.
 *
 * Instead of creating a react element, it returns result directly, so when
 * used by another component, it uses only DOM, without making the component's
 * hierarchy bigger.
 *
 * You should avoid it, unless profiling results convince you otherwise. This
 * inlining technique is cumbersome and performance benefits are visible only
 * for really deep hierarchies used in loops.
 *
 * Note: when using this, you need manually forward props that React handles
 * otherwise: `key` and `ref`.
 */
export const inlineElement = createOrCallElement
